import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import { CardInputsContainer } from "~/common/components";
import { getDisabledClasses } from "~/common/components/ui/FieldsetCard";
import Input from "~/common/components/ui/Input";
import { formatPhoneNumberField } from "~/common/utils/formatPhoneNumberField";
import { updatePatientPhoneNumberSchema } from "~/providers/schemas/patients";
import type { UpdatePatientPhoneNumberFormType } from "~/providers/types/patients";
import { getPatientInfoDefaultValues } from "~/providers/utils";
import { PatientData } from "~/providers/utils/types";

interface UpdatePatientAccountAccessFormProps {
  patient: PatientData;
}

export const UpdatePatientPhoneNumberForm = ({
  patient,
}: UpdatePatientAccountAccessFormProps) => {
  const isEditing = false;

  const {
    register,
    formState: { errors },
  } = useForm<UpdatePatientPhoneNumberFormType>({
    resolver: zodResolver(updatePatientPhoneNumberSchema),
    defaultValues: getPatientInfoDefaultValues(patient),
    mode: "all",
  });

  return (
    <CardInputsContainer
      title="Account access"
      className="md:max-w-full"
      contentClassName="flex flex-col gap-2"
    >
      <form className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Input
          label="Login email"
          {...getDisabledClasses(!isEditing)}
          {...register("email")}
          error={errors.email?.message}
          disabled
        />
        <Input
          label="Mobile number"
          {...getDisabledClasses(!isEditing)}
          {...register("mobileNumber")}
          {...formatPhoneNumberField<UpdatePatientPhoneNumberFormType>(
            "mobileNumber",
            register,
          )}
          error={errors.mobileNumber?.message}
          disabled={!isEditing}
        />
      </form>
      {isEditing && (
        <div className="grid grid-cols-1 md:grid-cols-2 md:gap-3">
          <p className="col-start-2 text-xs text-gray-20">{`Changing a user's mobile number will block access to the platform until the new number is verified.`}</p>
        </div>
      )}
    </CardInputsContainer>
  );
};
